import { cn } from '@/lib/utils';
import React from 'react';

type SkeletonProps = {
  className?: string;
  /**
   * Tag override. Defaults to `<div>`. Use `<span>` when nesting
   * inside inline content so flex / typography wrappers stay valid.
   */
  as?: 'div' | 'span';
} & React.HTMLAttributes<HTMLElement>;

/**
 * Single-shape skeleton block. Subtle pulsing, opacity-based fill so it
 * blends on light + dark backgrounds without needing per-theme classes.
 * Compose into page-level skeletons by chaining several of these in the
 * same shape as the real content underneath.
 *
 * Always pass an `aria-hidden` (default true) — skeletons are
 * decorative, the page-level loader provides the actual a11y status.
 */
export const Skeleton: React.FC<SkeletonProps> = ({ className, as: Tag = 'div', ...rest }) => (
  <Tag
    aria-hidden="true"
    className={cn('animate-pulse rounded-md bg-gray-950/[0.06] dark:bg-white/10', className)}
    {...rest}
  />
);

/**
 * A vertical stack of text-line skeletons. Last line is shorter to
 * read like a paragraph. `lines` defaults to 2.
 */
export const SkeletonText: React.FC<{
  lines?: number;
  className?: string;
}> = ({ lines = 2, className }) => (
  <div className={cn('flex flex-col gap-2', className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} className={cn('h-3', i === lines - 1 ? 'w-3/5' : 'w-full')} />
    ))}
  </div>
);

export default Skeleton;
